import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Briefcase, Gavel } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { getMatterPhaseColor } from '@/features/matters/statusStyles';
import type { MatterPhase } from '@/features/matters/types';
import { useLawyerMyMatters } from '../hooks/useLawyerMyMatters';
import { usePartnerMyMatters } from '../hooks/usePartnerMyMatters';
import { ShowMoreFooter, LIST_PAGE_SIZE } from './ShowMoreFooter';

const DAY_MS = 24 * 60 * 60 * 1000;

type RoleFilter = 'ALL' | 'LEAD' | 'CONTRIBUTOR';

function daysLeft(iso: string): number {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const due = new Date(iso);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - startOfToday.getTime()) / DAY_MS);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleString('en-US', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')}`;
}

function DueCell({ iso }: { iso: string | null }) {
  const { t } = useTranslation('common');
  if (!iso) return <span className="text-slate-400">—</span>;
  const days = daysLeft(iso);
  const cls =
    days < 0
      ? 'bg-red-50 text-red-700 border-red-100'
      : days <= 3
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-green-50 text-green-700 border-green-100';
  const label =
    days < 0
      ? t('dashboard.dueOverdue', { days: Math.abs(days) })
      : days === 0
        ? t('dashboard.dueToday')
        : t('dashboard.dueInDays', { days });
  return (
    <div className="flex items-center gap-2">
      <span className="font-medium text-slate-700 tabular-nums whitespace-nowrap">{formatDate(iso)}</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.6875rem] font-medium border whitespace-nowrap ${cls}`}>
        {label}
      </span>
    </div>
  );
}

const PAGE_SIZE = LIST_PAGE_SIZE;

interface MyMattersCardProps {
  /**
   * 'lawyer' (default) — Lead/Contributor matters with the role filter;
   * 'partner' — the partner's matters (role is always Partner, so the filter
   * and role column are hidden; task counts/deadlines are matter-wide).
   */
  variant?: 'lawyer' | 'partner';
}

export function MyMattersCard({ variant = 'lawyer' }: MyMattersCardProps) {
  const { t } = useTranslation('common');
  const isPartner = variant === 'partner';
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Role filter + paging both run server-side; each "Show more" click grows
  // the page by 10 — never the whole caseload at once (it can be huge).
  // Only the variant's query runs; both hooks keep the hook order stable.
  const lawyerQuery = useLawyerMyMatters({
    role: roleFilter === 'ALL' ? undefined : roleFilter,
    limit,
    enabled: !isPartner,
  });
  const partnerQuery = usePartnerMyMatters({ limit, enabled: isPartner });
  const { data: myMatters, isLoading } = isPartner ? partnerQuery : lawyerQuery;
  const rows = myMatters?.data ?? [];
  const hiddenCount = Math.max(0, (myMatters?.total ?? 0) - rows.length);

  const filters: { key: RoleFilter; label: string }[] = [
    { key: 'ALL', label: t('dashboard.filterAll') },
    { key: 'LEAD', label: t('dashboard.roleLead') },
    { key: 'CONTRIBUTOR', label: t('dashboard.roleContributor') },
  ];

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Briefcase size={16} className="text-slate-400" /> {t('dashboard.myMatters')}
            </CardTitle>
            <CardDescription className="mt-1.5">
              {isPartner ? t('dashboard.myMattersPartnerDesc') : t('dashboard.myMattersLawyerDesc')}
            </CardDescription>
          </div>
          {!isPartner && (
            <div className="flex w-fit shrink-0 items-center border border-slate-200 rounded-md shadow-xs overflow-hidden">
              {filters.map(f => (
                <button
                  key={f.key}
                  onClick={() => {
                    setRoleFilter(f.key);
                    setLimit(PAGE_SIZE); // new filter — start from the first page
                  }}
                  className={`px-3 py-1 border-r border-slate-200 last:border-r-0 text-[0.75rem] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    roleFilter === f.key
                      ? 'bg-[#E6F4FF] text-[#0069D1] font-semibold'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2 py-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400">
            <Briefcase size={28} className="mb-2 text-slate-300" />
            <span className="text-sm">{t('dashboard.noMyMatters')}</span>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="w-10 px-2 py-2.5" />
                  {[
                    t('dashboard.matter'),
                    t('client'),
                    // Partners are always Partner on their matters — the column is noise.
                    ...(isPartner ? [] : [t('dashboard.myRoleCol')]),
                    t('phase.label'),
                    isPartner ? t('dashboard.openTasksCol') : t('dashboard.myTasksCol'),
                    t('dashboard.nextDueCol'),
                  ].map(h => (
                    <th key={h} className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-2 py-2">
                      <Link
                        to={`/app/matters/${row.id}`}
                        title={t('dashboard.openMatter')}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-xs transition-colors hover:border-[#0069D1] hover:text-[#0069D1]"
                      >
                        <ArrowUpRight size={14} />
                      </Link>
                    </td>
                    <td className="px-2 py-2">
                      <Link to={`/app/matters/${row.id}`} className="font-semibold text-[#0069D1] hover:underline">
                        <span className="inline-flex items-center gap-1.5">
                          {row.isCourtCase && <Gavel size={13} className="shrink-0 text-slate-400" />}
                          {row.name}
                        </span>
                      </Link>
                    </td>
                    <td className="px-2 py-2">
                      {row?.clientId != null ? (
                        <Link to={`/app/clients/${row.clientId}`} className="text-slate-900 hover:underline">
                          {row?.clientName ?? '—'}
                        </Link>
                      ) : (
                        <span className="text-slate-900">{row?.clientName ?? '—'}</span>
                      )}
                    </td>
                    {!isPartner && (
                      <td className="px-2 py-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium border ${
                            row.myRole === 'LEAD'
                              ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                              : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}
                        >
                          {row.myRole === 'LEAD' ? t('dashboard.roleLead') : t('dashboard.roleContributor')}
                        </span>
                      </td>
                    )}
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium ${getMatterPhaseColor(row?.phase as MatterPhase)}`}>
                        {t(`phase.${row?.phase}`)}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      {row.myOpenTasks > 0 ? (
                        <span className="font-semibold tabular-nums text-slate-900">
                          {row.myOpenTasks}{' '}
                          <span className="font-normal text-[0.75rem] text-slate-400">{t('dashboard.openTasksShort')}</span>
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <DueCell iso={row.nextDueAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ShowMoreFooter
              hiddenCount={hiddenCount}
              visibleCount={rows.length}
              onShowMore={() => setLimit(l => l + PAGE_SIZE)}
              onShowLess={() => setLimit(PAGE_SIZE)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
